/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
import React, { useState } from "react";
import MaterialTable, { Column } from "@material-table/core";

import { Button, Icon, Tooltip, Typography } from "@material-ui/core";
import { AccessibleForward, AddToPhotos, Hearing, Visibility } from "@material-ui/icons";
import { useMutation, useQuery } from "@apollo/client";
import { omit, pick } from "lodash";
import { Modal } from "components/haven-design";
import { DialogActions, DialogContent } from "components/haven-design/modal/dialog";
import { unitSizeMetadata } from "model/interest/unit-size";
import {
  CreateBuildingUnit,
  DeleteBuildingUnit,
  ListBuildingUnits,
  ListBuildingUnits_buildingUnits as BuildingUnitType,
  UpdateBuildingUnit,
} from "types/gql-types";
import { useSnackbar } from "components/haven-design/snackbar";
import { LoadingSpinner, ServerError } from "components/graphql-views";
import UnitForm from "./unit-add-form";
import BatchUnitForm from "./unit-batch-add-form";
import BatchUnitEditForm, { ToggleSwitches } from "./unit-batch-edit-form";
import { CREATE_BUILDING_UNIT, DELETE_BUILDING_UNIT, QUERY_BUILDING_UNITS, UPDATE_BUILDING_UNIT } from "./query";
import { BuildingUnitSharedType } from "./unit-form-shared-fields";


const unitSizeLookup = unitSizeMetadata
  .reduce((acc, { id, label }) => ({ ...acc, [id]: label }), {} as Record<string, string>);

const rentLabel = ({ rentType, rentValue }: BuildingUnitType) => (
  rentType === "relative" ? `${rentValue}%` : `$${rentValue}`);
const columns: Array<Column<BuildingUnitType>> = [
  { title: "Unit Name", field: "name" },
  { title: "Bedrooms", field: "bedrooms", lookup: unitSizeLookup },
  { title: "Bathrooms", field: "bathrooms" },
  { title: "Occupancy", field: "occupancy", render: ({ occupancyMin, occupancyMax }) => `${occupancyMin} - ${occupancyMax}` },
  { title: "Rent",
    field: "rent",
    render: rentLabel,
    customFilterAndSearch: (term, unitData) =>
      rentLabel(unitData).indexOf(term.toLowerCase()) !== -1 },
  { title: "Designation", field: "incomeDesignation" },
  { title: "Vacancy Status",
    field: "occupied" },
  { title: "Pref",
    render: ({ preference }) => <>{preference.join(", ")}</> },
  { title: "Accessibility",
    field: "accessibility",
    render: ({ accessibility }) => {
      const toIcon = (option: string | null) => {
        switch (option) {
          case "mobility":
            return <Tooltip title="Mobility"><Icon><AccessibleForward/></Icon></Tooltip>;
          case "hearing":
            return <Tooltip title="Hearing"><Icon><Hearing/></Icon></Tooltip>;
          case "visual":
            return <Tooltip title="Visual"><Icon><Visibility/></Icon></Tooltip>;
          default:
            return null;
        }
      };
      return <>{accessibility.map((accessibilityOption) => toIcon(accessibilityOption))}</>;
    } },
  { title: "Sqft", field: "sqft" },
  { title: "Floor", field: "floor" },
  { title: "Funding Source",
    render: ({ fundingSource }) => <>{fundingSource.join(", ")}</> },
];

const UnitList: React.FC<{buildingId: string}> = ({ buildingId }) => {
  const { openSnackbar } = useSnackbar();
  const { loading, data, error } = useQuery<ListBuildingUnits>(QUERY_BUILDING_UNITS, {
    variables: { buildingId },
    fetchPolicy: "network-only",
  });
  const [createBuildingUnitMutation, { loading: createLoading }] =
    useMutation<CreateBuildingUnit>(CREATE_BUILDING_UNIT, { refetchQueries: ["ListBuildingUnits"] });
  const [updateBuildingUnitMutation, { loading: updateLoading }] =
    useMutation<UpdateBuildingUnit>(UPDATE_BUILDING_UNIT, { refetchQueries: ["ListBuildingUnits"] });
  const [deleteBuildingUnitMutation, { loading: deleteLoading }] =
    useMutation<DeleteBuildingUnit>(DELETE_BUILDING_UNIT, { refetchQueries: ["ListBuildingUnits"] });

  const createBuildingUnit =
  (buildingUnit: BuildingUnitSharedType & {name: string}, snackbar = true) =>
    createBuildingUnitMutation({ variables: { buildingUnit: { ...buildingUnit, buildingId } } })
      .then(() => {
        if (snackbar) openSnackbar({ message: "New Unit Successfully Added", status: "success" });
      });
  const handleAddUnit = createBuildingUnit;

  const createBatchBuildingUnit = (unitNames: string[], sharedValues: BuildingUnitSharedType) => {
    Promise.all(unitNames.map((unitName) => createBuildingUnit({
      name: unitName,
      ...sharedValues,
    }, false)))
      .then(() => openSnackbar({ message: "New Units Successfully Added", status: "success" }));
  };
  const handleBatchAddUnit = createBatchBuildingUnit;

  const deleteBuildingUnit = (id: string, snackbar = true) =>
    deleteBuildingUnitMutation({ variables: { id } })
      .then(() => {
        if (snackbar) openSnackbar({ message: "Unit Successfully Removed", status: "success" });
      });

  const deleteBatchBuildingUnit = (ids: string[]) =>
    Promise.all(ids.map((id) => deleteBuildingUnit(id, false)))
      .then(() => openSnackbar({ message: "Units Successfully Removed", status: "success" }));
  const deleteUnits = deleteBatchBuildingUnit;

  const updateBuildingUnit = (buildingUnit: Partial<BuildingUnitType>, snackbar = true) =>
    updateBuildingUnitMutation({ variables: {
      id: buildingUnit.id,
      buildingUnit: omit(buildingUnit, ["id", "__typename"]),
    } })
      .then(() => {
        if (snackbar) openSnackbar({ message: "Unit Successfully Updated", status: "success" });
      });

  const [unitAddOpen, setUnitAddOpen] = useState(false);
  const openAddModal = () => setUnitAddOpen(true);
  const closeAddModal = () => setUnitAddOpen(false);
  const [unitAddBatchOpen, setUnitAddBatchOpen] = useState(false);
  const openAddBatchModal = () => setUnitAddBatchOpen(true);
  const closeAddBatchModal = () => setUnitAddBatchOpen(false);
  const [unitsToDelete, setUnitsToDelete] = useState<string[]>([]);
  const handleDeleteOpen = (items: BuildingUnitType[]) =>
    setUnitsToDelete(items.map(({ id }) => id));
  const handleDeleteClose = () => setUnitsToDelete([]);
  const [selectedUnits, selectUnits] = useState<BuildingUnitType[]>([]);

  const handleBatchUpdateUnit = (
    items: BuildingUnitType[],
    sharedValues: Partial<BuildingUnitType>,
    toggleSwitches: ToggleSwitches,
  ) => {
    if (items.length === 1) {
      const [item] = items;
      updateBuildingUnit({ ...item, ...sharedValues });
    } else {
      const fieldNames = Object.keys(sharedValues);
      const keysToUpdate = Object.entries(toggleSwitches).map(([key, enabled]) => {
        if (!enabled) return [];
        return fieldNames.filter((fieldName) => fieldName.toLowerCase().includes(key));
      }).flat();
      const valuesToUpdate = pick(sharedValues, keysToUpdate);
      Promise.all(items.map((item) =>
        updateBuildingUnit({ id: item.id, ...valuesToUpdate }, false)))
        .then(() => openSnackbar({ message: "Unit Successfully Updated", status: "success" }));
    }
  };
  const closeEditModal = () => selectUnits([]);


  if (loading) return <LoadingSpinner />;
  if (error) return <ServerError desc={error.message} />;
  if (!data) return <ServerError desc="Missing Data" />;

  return <>
    <UnitForm handleAddUnit={handleAddUnit} open={unitAddOpen} closeModal={closeAddModal}/>
    <BatchUnitForm
      handleBatchAddUnit={handleBatchAddUnit}
      open={unitAddBatchOpen}
      closeModal={closeAddBatchModal}
    />
    <BatchUnitEditForm
      handleBatchUpdateUnit={handleBatchUpdateUnit}
      isOpen={selectedUnits.length > 0}
      closeModal={closeEditModal}
      items={selectedUnits}
    />
    <MaterialTable
      components={{
        Container: (containerProps) => <div {...containerProps}/>,
      }}
      data={data.buildingUnits}
      columns={columns}
      title="Units"
      options={{
        selection: true,
        pageSize: 10,
      }}
      actions={[
        {
          icon: "add",
          tooltip: "Add Unit",
          isFreeAction: true,
          onClick: openAddModal,
          disabled: createLoading || updateLoading || deleteLoading,
        },
        {
          icon: AddToPhotos,
          tooltip: "Add Multiple Units",
          isFreeAction: true,
          onClick: openAddBatchModal,
          disabled: createLoading || updateLoading || deleteLoading,
        },
        {
          tooltip: "Bulk Edit Selected Units",
          icon: "edit",
          onClick: (_event, selectedData) => {
            if (Array.isArray(selectedData)) {
              selectUnits(selectedData.map((item) => omit(item, ["tableData"]) as BuildingUnitType));
            }
          },
        },
        {
          tooltip: "Remove All Selected Units",
          icon: "delete",
          onClick: (_event, selectedData) => {
            if (Array.isArray(selectedData)) handleDeleteOpen(selectedData);
          },
        },
      ]}
    />
    <Modal
      isOpen={unitsToDelete.length > 0}
      onRequestClose={handleDeleteClose}
      title={`Delete Unit${unitsToDelete.length > 1 ? "s" : ""}`}
    >
      <DialogContent>
        <Typography variant="subtitle1">
          Are you sure you want to delete the selected unit{unitsToDelete.length > 1 ? "s" : ""}?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button size="large" color="secondary" variant="outlined" onClick={handleDeleteClose}>
          Cancel
        </Button>
        <Button
          size="large"
          color="primary"
          variant="contained"
          className="color-bk-error"
          onClick={() => {
            deleteUnits(unitsToDelete);
            handleDeleteClose();
          }}
        >
          Delete Unit{unitsToDelete.length > 1 ? "s" : ""}
        </Button>
      </DialogActions>
    </Modal>
  </>;
};

export default UnitList;
